import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import useAuth from '../hooks/useAuth';
import { getFinancialStats, getActiveResourceInfo, getMissionAlerts } from '../api/api';
import MissionDetailsModal from '../components/MissionDetailsModal';
import OnboardingModal from '../components/OnboardingModal';
import styles from './AppHomePage.module.css';

const AppHomePage = () => {
  const { user } = useAuth();
  const { id: impersonatedId } = useParams();
  const location = useLocation();
  
  const passedUser = location.state?.impersonatedUser;

  const isImpersonating = !!impersonatedId && user?.type === 'Admin';
  const targetUserId = isImpersonating ? impersonatedId : user?.id; 

  const tabsRef = useRef(null);
  
  const [financials, setFinancials] = useState([]);
  const [activeInfo, setActiveInfo] = useState({ 
    clients: 'Chargement...', 
    tjm: '...', 
    current_gestion_rate: 0,
    title: ''
  });
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);
  const [missionAlerts, setMissionAlerts] = useState([]);
  const [selectedMissionId, setSelectedMissionId] = useState(null);

  // State pour le filtre historique
  const [historyClientFilter, setHistoryClientFilter] = useState('Tous');

  const criticalAlerts = missionAlerts.filter(m => m.status === 'alerte' && m.daysRemaining <= 15);

  // Formatteurs
  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const toggleRow = (period) => {
    setExpandedRows(prev => ({ ...prev, [period]: !prev[period] }));
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'payée' || s.includes('paid')) return styles.badgeSuccess;
    if (s.includes('retard') || s.includes('late')) return styles.badgeDanger;
    if (s.includes('envoyée') || s.includes('sent')) return styles.badgeInfo;
    return styles.badgeWarning;
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString('fr-FR');
    
    doc.setFillColor(67, 94, 190);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text("RAPPORT D'ACTIVITÉ", 20, 25);
    doc.setFontSize(10);
    
    const nameDisplay = isImpersonating 
        ? `${passedUser?.prenom || 'Consultant'} ${passedUser?.lastName || ''}` 
        : `${user?.prenom} ${user?.lastName}`;
        
    doc.text(`Généré le ${dateStr} pour ${nameDisplay}`, 20, 32);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text("Résumé des Soldes", 20, 55);
    
    autoTable(doc, {
      startY: 60,
      head: [['Désignation', 'Montant']],
      body: [
        ['Solde Réel (Encaissé)', formatCurrency(totalSoldeReel)],
        ['Solde Prévisionnel', formatCurrency(totalSoldePrevisionnel)],
        ['Reste à Encaisser', formatCurrency(totalSoldePrevisionnel - totalSoldeReel)],
      ],
      theme: 'striped',
      headStyles: { fillStyle: [67, 94, 190] }
    });

    const historyBody = financials.slice(0, 12).map(item => [
      item.period,
      formatCurrency(item.revenue_normal + item.revenue_hno),
      formatCurrency(item.net_transfer),
      item.revenue_normal_paid + item.revenue_hno_paid >= item.revenue_normal + item.revenue_hno ? 'Payé' : 'Partiel'
    ]);

    doc.text("Historique des 12 derniers mois (Synthèse)", 20, doc.lastAutoTable.finalY + 15);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Période', 'CA Total', 'Virement Net', 'Statut']],
      body: historyBody,
      styles: { fontSize: 9 }
    });

    if (missionAlerts.length > 0) {
      doc.text("État des Missions", 20, doc.lastAutoTable.finalY + 15);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Partenaire', 'Fin de mission', 'Jours Restants', 'Statut']],
        body: missionAlerts.map(m => [
          m.clientName, 
          new Date(m.endDate).toLocaleDateString('fr-FR'), 
          `${m.daysRemaining} j`,
          m.status.toUpperCase()
        ]),
        headStyles: { fillStyle: [231, 76, 60] }
      });
    }

    doc.save(`rapport_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (targetUserId) {
        try {
          setLoading(true);
          const [financeRes, infoRes, alertsRes] = await Promise.all([
            getFinancialStats(targetUserId),
            getActiveResourceInfo(targetUserId),
            getMissionAlerts(targetUserId)
          ]);
          
          const rawData = financeRes.data || [];
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1;
          
          const isProduction = process.env.REACT_APP_ENV === 'Production';

          const filteredData = rawData.filter(item => {
             if (isProduction && item.year < 2026) return false;
             if (item.year < currentYear) return true;
             if (item.year === currentYear && item.month < currentMonth) return true;
             return false;
          });

          setFinancials(filteredData);
          setActiveInfo(infoRes.data || {});
          setMissionAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
        } catch (error) {
          console.error("Erreur dashboard:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [targetUserId]);

  const getMissionStatusConfig = (mission) => {
    if (mission.status === 'terminee') return { class: styles.missionTerminee, icon: '🏁', label: 'Terminée' };
    if (mission.status === 'future') return { class: styles.missionFuture, icon: '🔜', label: 'À venir' };
    
    const days = mission.daysRemaining;
    if (days <= 15) return { class: styles.missionCritique, icon: '🛑', label: 'Fin imminente' };
    if (days <= 30) return { class: styles.missionAlerte, icon: '⚠️', label: 'Fin proche' };
    if (days <= 60) return { class: styles.missionInfo, icon: 'ℹ️', label: 'Fin < 2 mois' };
    
    return { class: styles.missionActive, icon: '✅', label: 'En cours' };
  };

  const { totalSoldeReel, totalSoldePrevisionnel } = useMemo(() => {
    return financials.reduce((acc, item) => {
      const caPaid = item.revenue_normal_paid + item.revenue_hno_paid;
      const caTotal = item.revenue_normal + item.revenue_hno;

      let fraisGestion = 0;

      if (item.missions_details && item.missions_details.length > 0) {
          fraisGestion = item.missions_details.reduce((sum, mission) => {
              const missionBase = mission.isNDF ? 0 : (mission.detail_normal || 0);
              const activeRate = (mission.taux_specifique !== undefined && mission.taux_specifique !== null) 
                  ? mission.taux_specifique 
                  : item.gestion_rate;
              return sum + (missionBase * (activeRate / 100));
          }, 0);
      } else {
          const baseGestion = item.revenue_normal_hors_ndf !== undefined ? item.revenue_normal_hors_ndf : item.revenue_normal;
          fraisGestion = baseGestion * (item.gestion_rate / 100);
      }

      const chargesPaid = item.employer_cost + fraisGestion + item.expenses + item.net_transfer;
      const chargesTotal = item.employer_cost + fraisGestion + item.expenses + item.net_transfer;

      acc.totalSoldeReel += (caPaid - chargesPaid + (item.ajustement || 0));
      acc.totalSoldePrevisionnel += (caTotal - chargesTotal + (item.ajustement || 0));
      
      return acc;
    }, { totalSoldeReel: 0, totalSoldePrevisionnel: 0 });
  }, [financials]);

  const last6Months = financials.slice(0, 6).reverse();
  const chartData = last6Months.map(item => ({
    name: item.period,
    CA: item.revenue_normal + item.revenue_hno,
    Dépenses: item.employer_cost + item.expenses,
    Virement: item.net_transfer
  }));

  const selectedMonth = financials[selectedMonthIndex] || {};
  const selectedMonthCA = (selectedMonth.revenue_normal || 0) + (selectedMonth.revenue_hno || 0);
  const selectedMonthCAPaid = (selectedMonth.revenue_normal_paid || 0) + (selectedMonth.revenue_hno_paid || 0);
  
  const pieData = useMemo(() => {
    const data = [
      { name: 'CA Facturé', value: selectedMonthCA || 0, color: '#9b59b6' },
      { name: 'CA Encaissé', value: selectedMonthCAPaid || 0, color: '#f1c40f' },
      { name: 'NDF', value: selectedMonth.expenses || 0, color: '#3498db' },
      { name: 'Virement Net', value: selectedMonth.net_transfer || 0, color: '#27ae60' }
    ];
    return data.filter(item => item.value > 0);
  }, [selectedMonth, selectedMonthCA, selectedMonthCAPaid]);

  // Logique de filtrage pour l'historique
  const uniqueHistoryClients = useMemo(() => {
    const clients = new Set();
    financials.forEach(item => {
      if (item.missions_details && Array.isArray(item.missions_details)) {
        item.missions_details.forEach(m => {
            if(m.clientName) clients.add(m.clientName);
        });
      }
    });
    return ['Tous', ...Array.from(clients).sort()];
  }, [financials]);

  const filteredHistoryData = useMemo(() => {
    if (historyClientFilter === 'Tous') {
      return financials;
    }

    return financials.map(month => {
      const clientDetails = (month.missions_details || []).filter(
        m => m.clientName === historyClientFilter
      );

      if (clientDetails.length === 0) return null;

      const clientRevenueNormal = clientDetails.reduce((sum, m) => sum + (m.detail_normal || 0), 0);
      const clientRevenueNormalHorsNdf = clientDetails.reduce((sum, m) => sum + (m.isNDF ? 0 : (m.detail_normal || 0)), 0);
      const clientRevenueHno = clientDetails.reduce((sum, m) => sum + (m.detail_hno || 0), 0);
      const clientTotalRevenue = clientRevenueNormal + clientRevenueHno;

      const clientPaidAmount = clientDetails.reduce((sum, m) => {
        const status = (m.stateFacture || '').toLowerCase();
        const isPaid = status.includes('payée') || status.includes('paid');
        return sum + (isPaid ? ((m.detail_normal || 0) + (m.detail_hno || 0)) : 0);
      }, 0);

      const monthTotalRevenue = month.revenue_normal + month.revenue_hno;
      const ratio = monthTotalRevenue > 0 ? (clientTotalRevenue / monthTotalRevenue) : 0;

      return {
        ...month,
        period: month.period,
        missions_details: clientDetails,
        revenue_normal: clientRevenueNormal,
        revenue_normal_hors_ndf: clientRevenueNormalHorsNdf,
        revenue_hno: clientRevenueHno,
        revenue_normal_paid: clientPaidAmount, 
        revenue_hno_paid: 0, 
        employer_cost: month.employer_cost * ratio,
        expenses: month.expenses * ratio,
        net_transfer: month.net_transfer * ratio,
        ajustement: (month.ajustement || 0) * ratio, 
        motif_ajustement: month.motif_ajustement,
        gestion_rate: month.gestion_rate 
      };
    }).filter(Boolean);
  }, [financials, historyClientFilter]);

  // Vérifier si on doit afficher la colonne HNO
  const globalHasHNO = useMemo(() => {
    return filteredHistoryData.some(item => (item.revenue_hno > 0) || (item.missions_details || []).some(m => m.detail_hno > 0));
  }, [filteredHistoryData]);

  // ✅ CORRECTION : Vérifier si au moins une ligne a un ajustement non nul
  const hasAnyAjustement = useMemo(() => {
    return filteredHistoryData.some(item => item.ajustement && item.ajustement !== 0);
  }, [filteredHistoryData]);

  const views = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
    { id: 'performance', label: 'Performance', icon: '📈' },
    { id: 'history', label: 'Historique', icon: '📜' },
    { id: 'missions', label: 'Missions', icon: '💼' }
  ];

  const handleManageMissions = () => {
    setActiveView('missions');
    if (tabsRef.current) {
      tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return <div className={styles.loading}>Chargement de vos données...</div>;
  }

  const displayTitle = isImpersonating 
    ? (passedUser?.title || activeInfo.title || 'Consultant') 
    : (activeInfo.title || user?.title);

  const displayWelcome = isImpersonating
    ? (passedUser ? `Vue simulée : ${passedUser.prenom} ${passedUser.lastName}` : `Vue Consultant (Simulée)`)
    : `Bonjour, ${user?.prenom} 👋`;

  const showOnboarding = user && !user.is_onboarded && user.type !== 'Admin' && !isImpersonating;

  return (
    <div className={styles.container}>
      {showOnboarding && <OnboardingModal />}
      {isImpersonating && (
        <div style={{ backgroundColor: '#e74c3c', color: 'white', padding: '12px 20px', marginBottom: '20px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <span style={{fontSize: '1.2em'}}>👁️</span>
                <span>MODE VUE CONSULTANT (ID: {targetUserId})</span>
            </div>
            <Link to={`/admin/resources/${targetUserId}`} style={{ backgroundColor: 'white', color: '#e74c3c', padding: '8px 16px', textDecoration: 'none', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                Quitter la simulation
            </Link>
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.infoSection}>
          <div className={styles.userProfile}>
            <h1 className={styles.welcomeTitle}>{displayWelcome}</h1>
            <p className={styles.userTitle}>{displayTitle}</p>
            <div className={styles.tagCloud}>
              <span className={styles.roleTag}>{isImpersonating ? 'Consultant (Simulé)' : user?.type}</span>
            </div>
          </div>
        </div>

        <div className={styles.statsSection}>
          <div className={styles.activeMissionCard}>
            <div className={styles.cardHeaderSimple}>
              <span className={styles.statLabel}>Missions en cours</span>
            </div>
            
            <div className={styles.missionList}>
              {activeInfo.clients ? (
                activeInfo.clients.split('/').map((clientName, index) => {
                  const rawTjm = String(activeInfo.tjm || '0');
                  const tjmList = rawTjm.split('/').map(t => t.trim());
                  const currentTjm = tjmList[index] || tjmList[0];

                  return (
                    <div key={index} className={styles.missionRow}>
                      <div className={styles.clientBadgePrimary}>
                        <span className={styles.dot}></span>
                        <span className={styles.clientName}>{clientName.trim()}</span>
                      </div>
                      <div className={styles.tjmContainer}>
                        <span className={styles.tjmValueRow}>{currentTjm}€</span>
                        <span className={styles.tjmUnitRow}>/ jour</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <span className={styles.noClient}>Aucun partenaire actif</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {criticalAlerts.length > 0 && (
        <div className={styles.simpleNotification}>
          <span className={styles.notifIcon}>ℹ️</span>
          <p className={styles.notifText}>
            Vous avez <strong>{criticalAlerts.length} mission(s)</strong> arrivant à échéance prochainement.
          </p>
          <button onClick={handleManageMissions} className={styles.notifLink}>
            Voir les détails
          </button>
        </div>
      )}

      <div className={styles.balanceCards}>
        <div className={styles.balanceCard + ' ' + styles.balanceCardPrimary}>
          <div className={styles.balanceHeader}>
            <span className={styles.balanceIcon}>💰</span>
            <span className={styles.balanceLabel}>Solde Disponible</span>
          </div>
          <div className={styles.balanceAmount}>{formatCurrency(totalSoldeReel)}</div>
          <div className={styles.balanceFooter}>
            <span className={styles.balanceTag}>Encaissé</span>
          </div>
        </div>

        <div className={styles.balanceCard + ' ' + styles.balanceCardSecondary}>
          <div className={styles.balanceHeader}>
            <span className={styles.balanceIcon}>📊</span>
            <span className={styles.balanceLabel}>Solde Prévisionnel</span>
          </div>
          <div className={styles.balanceAmount}>{formatCurrency(totalSoldePrevisionnel)}</div>
          <div className={styles.balanceFooter}>
            <span className={styles.balanceTag}>Total facturé</span>
          </div>
        </div>

        <div className={styles.balanceCard + ' ' + styles.balanceCardInfo}>
          <div className={styles.balanceHeader}>
            <span className={styles.balanceIcon}>💸</span>
            <span className={styles.balanceLabel}>Solde en attente</span>
          </div>
          <div className={styles.balanceAmount}>{formatCurrency(totalSoldePrevisionnel - totalSoldeReel)}</div>
          <div className={styles.balanceFooter}>
            <span className={styles.balanceTag}>En attente</span>
          </div>
        </div>
      </div>

      <div className={styles.tabsContainer} ref={tabsRef}>
        {views.map(view => (
          <button
            key={view.id}
            onClick={() => setActiveView(view.id)}
            className={`${styles.tab} ${activeView === view.id ? styles.tabActive : ''}`}
          >
            <span className={styles.tabIcon}>{view.icon}</span>
            {view.label}
            {view.id === 'missions' && missionAlerts.filter(m => m.status === 'alerte').length > 0 && (
              <span style={{ marginLeft: '8px', backgroundColor: '#e74c3c', color: 'white', borderRadius: '50%', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                {missionAlerts.filter(m => m.status === 'alerte').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeView === 'overview' && (
        <div className={styles.content}>
            {financials.length > 0 && (
            <div className={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                  Analyse du mois - {selectedMonth.period || 'N/A'}
                </h2>
                <select 
                  value={selectedMonthIndex} 
                  onChange={(e) => setSelectedMonthIndex(Number(e.target.value))}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: '2px solid #667eea', fontSize: '1rem', fontWeight: '600', color: '#667eea', backgroundColor: 'white', cursor: 'pointer', outline: 'none'
                  }}
                >
                  {financials.map((item, index) => (
                    <option key={item.period} value={index}>
                      {item.period}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          <div className={styles.section}>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIcon}>💼</div>
                <div className={styles.kpiContent}>
                  <span className={styles.kpiLabel}>CA Facturé</span>
                  <span className={styles.kpiValue}>{formatCurrency(selectedMonthCA)}</span>
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIcon}>✅</div>
                <div className={styles.kpiContent}>
                  <span className={styles.kpiLabel}>CA Encaissé</span>
                  <span className={styles.kpiValue}>{formatCurrency(selectedMonthCAPaid)}</span>
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIcon}>💳</div>
                <div className={styles.kpiContent}>
                  <span className={styles.kpiLabel}>Virement Net</span>
                  <span className={styles.kpiValue}>{formatCurrency(selectedMonth.net_transfer)}</span>
                </div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIcon}>📄</div>
                <div className={styles.kpiContent}>
                  <span className={styles.kpiLabel}>Frais (NDF)</span>
                  <span className={styles.kpiValue}>{formatCurrency(selectedMonth.expenses)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Répartition des revenus</h2>
            <div className={styles.chartContainer}>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" labelLine={true} label={({ name, value }) => `${name}: ${formatCurrency(value)}`} outerRadius={100} dataKey="value">
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noDataPlaceholder}>
                  <p>Aucune donnée financière pour cette période</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeView === 'missions' && (
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.sectionHeaderFlex}>
              <h2 className={styles.sectionTitle}>Toutes mes missions</h2>
              <button onClick={exportToPDF} className={styles.pdfBtn}>📄 Exporter en PDF</button>
            </div>

            <div className={styles.missionGrid}>
              {missionAlerts.map((mission, index) => {
                const config = getMissionStatusConfig(mission);
                const totalDays = Math.ceil((new Date(mission.endDate) - new Date(mission.startDate)) / (86400000));
                const progress = mission.daysRemaining > 0 ? Math.max(0, Math.min(100, 100 - (mission.daysRemaining / totalDays * 100))) : 100;

                return (
                  <div key={index} className={`${styles.missionCard} ${config.class}`}>
                    <div className={styles.missionBadge}>
                      <span>{config.icon}</span> {config.label}
                    </div>
                    <div className={styles.missionMainInfo}>
                      <h3 className={styles.clientName}>{mission.clientName}</h3>
                      <p className={styles.tjmText}>TJM: {mission.TJM}€</p>
                      <button className={styles.invoiceBtn} onClick={() => { setSelectedMissionId(mission.prestation_id); }}>
                        📄 Voir factures
                      </button>
                    </div>
                    <div className={styles.missionDatesGrid}>
                      <div><p className={styles.dateLabel}>Début</p><p className={styles.dateVal}>{new Date(mission.startDate).toLocaleDateString()}</p></div>
                      <div><p className={styles.dateLabel}>Fin</p><p className={styles.dateVal}>{new Date(mission.endDate).toLocaleDateString()}</p></div>
                      <div><p className={styles.dateLabel}>Restant</p><p className={styles.dateVal}>{mission.daysRemaining > 0 ? `${mission.daysRemaining}j` : '-'}</p></div>
                    </div>
                    {mission.status !== 'terminee' && (
                      <div className={styles.progressWrapper}>
                        <div className={styles.progressBar}><div style={{ width: `${progress}%` }} className={styles.progressFill}></div></div>
                        <p className={styles.progressText}>{progress.toFixed(0)}% effectué</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeView === 'performance' && (
        <div className={styles.content}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Évolution sur 6 mois</h2>
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="CA" stroke="#3498db" strokeWidth={3} name="Chiffre d'affaires" />
                  <Line type="monotone" dataKey="Virement" stroke="#27ae60" strokeWidth={3} name="Virement net" />
                  <Line type="monotone" dataKey="Dépenses" stroke="#e74c3c" strokeWidth={3} name="Charges" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Comparaison mensuelle</h2>
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="CA" fill="#3498db" name="CA Facturé" />
                  <Bar dataKey="Virement" fill="#27ae60" name="Virement Net" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {activeView === 'history' && (
        <div className={styles.content}>
          <div className={styles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Historique détaillé</h2>
                
                {uniqueHistoryClients.length > 2 && ( 
                    <div style={{display:'flex', alignItems: 'center', gap: '8px'}}>
                        <span style={{color: '#666', fontSize: '0.9rem'}}>Filtre :</span>
                        <select 
                            value={historyClientFilter}
                            onChange={(e) => setHistoryClientFilter(e.target.value)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#f9fafb',
                                color: '#374151',
                                fontSize: '0.9rem',
                                cursor: 'pointer'
                            }}
                        >
                            {uniqueHistoryClients.map(client => (
                                <option key={client} value={client}>{client}</option>
                            ))}
                        </select>
                    </div>
                )}
              </div>

              <button 
                onClick={exportToPDF}
                style={{
                  padding: '12px 24px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#c0392b'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#e74c3c'}
              >
                📄 Exporter en PDF
              </button>
            </div>
            
            {filteredHistoryData.length > 0 ? (
              <div className={styles.tableContainer}>
                {historyClientFilter !== 'Tous' && (
                  <div style={{
                    padding: '8px 12px', 
                    marginBottom: '10px', 
                    backgroundColor: '#eef2ff', 
                    border: '1px solid #c7d2fe', 
                    borderRadius: '6px', 
                    color: '#3730a3', 
                    fontSize: '0.9rem'
                  }}>
                    ℹ️ <strong>Mode filtré :</strong> Les charges (Coût employeur, NDF, Virements) sont affichées au <strong>prorata</strong> du CA généré par {historyClientFilter} pour conserver la cohérence du calcul de solde.
                  </div>
                )}
                
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Période</th>
                      <th>Partenaire</th>
                      <th>CA Facturé (Hors HNO)</th>
                      <th>CA Encaissé</th>
                      <th>Coût Employeur</th>
                      <th>Frais Gestion</th>
                      {/* ✅ CORRECTION : Colonne Ajustement affichée seulement si données présentes */}
                      {hasAnyAjustement && <th>Ajust. Except.</th>}
                      <th>Virement Net</th>
                      <th>NDF</th>
                      {globalHasHNO && <th>HNO</th>}
                      <th>Total</th>
                      <th>Reste Mensuel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistoryData.map(item => {
                      const caTotal = item.revenue_normal + item.revenue_hno;
                      const caPaid = item.revenue_normal_paid + item.revenue_hno_paid;

                      let fraisGestion = 0;

                      if (item.missions_details && item.missions_details.length > 0) {
                          fraisGestion = item.missions_details.reduce((sum, mission) => {
                              const missionBase = mission.isNDF ? 0 : (mission.detail_normal || 0);
                              const activeRate = (mission.taux_specifique !== undefined && mission.taux_specifique !== null) 
                                  ? mission.taux_specifique 
                                  : item.gestion_rate;
                              return sum + (missionBase * (activeRate / 100));
                          }, 0);
                      } else {
                          const baseGestion = item.revenue_normal_hors_ndf !== undefined ? item.revenue_normal_hors_ndf : item.revenue_normal;
                          fraisGestion = baseGestion * (item.gestion_rate / 100);
                      }

                      const totalCharges = item.employer_cost + fraisGestion + item.expenses + item.net_transfer;
                      const soldeReel = caPaid - totalCharges + (item.ajustement || 0);
                      
                      const isPaid = Math.abs(caPaid - caTotal) < 1; 
                      
                      const hasDetails = item.missions_details && item.missions_details.length > 0;
                      const isExpanded = expandedRows[item.period];
                      const hasHNO = hasDetails && item.missions_details.some(m => m.detail_hno && m.detail_hno > 0);

                      const hnoAmount = item.revenue_hno || 0;
                      const customTotal = item.employer_cost + fraisGestion + item.net_transfer + item.expenses - (item.ajustement || 0);
                      
                      return (
                        <React.Fragment key={item.period}>
                          <tr className={isExpanded ? styles.expandedRow : ''}>
                            <td 
                              style={{
                                fontWeight: 'bold', 
                                cursor: hasDetails ? 'pointer' : 'default',
                                userSelect: 'none'
                              }}
                              onClick={() => hasDetails && toggleRow(item.period)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span 
                                  style={{
                                    display: 'inline-block',
                                    width: '20px', 
                                    color: hasDetails ? 'inherit' : '#ccc',
                                    visibility: hasDetails ? 'visible' : 'hidden',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </span>
                                <span>{item.period}</span>
                              </div>
                            </td>

                            <td>
                              {historyClientFilter !== 'Tous' 
                                ? <span className={styles.clientBadge}>{historyClientFilter}</span>
                                : (hasDetails ? Array.from(new Set(item.missions_details.map(m => m.clientName))).filter(Boolean).join(', ') : 'N/A')
                              }
                            </td>

                            <td>{formatCurrency(item.revenue_normal)}</td>

                            <td style={{
                                fontWeight: 'bold',
                                color: caTotal === 0 ? '#e74c3c' : (isPaid ? '#27ae60' : '#f39c12')
                            }}>
                                {formatCurrency(caPaid)}
                            </td>
                            
                            <td>{formatCurrency(item.employer_cost)}</td>
                            
                            <td>{formatCurrency(fraisGestion)}</td>

                            {/* ✅ CORRECTION : Cellule Ajustement affichée seulement si la colonne existe */}
                            {hasAnyAjustement && (
                              <td>
                                <div style={{display:'flex', flexDirection:'column', justifyContent:'center'}}>
                                  <span style={{fontWeight:'bold', color: item.ajustement > 0 ? '#27ae60' : (item.ajustement < 0 ? '#c0392b' : '#7f8c8d')}}>
                                    {item.ajustement ? formatCurrency(item.ajustement) : '-'}
                                  </span>
                                  {item.motif_ajustement && (
                                    <span style={{fontSize:'0.7rem', color:'#95a5a6', fontStyle:'italic', marginTop:'2px', lineHeight: '1.1'}}>
                                      {item.motif_ajustement}
                                    </span>
                                  )}
                                </div>
                              </td>
                            )}

                            <td style={{fontWeight: 'bold', backgroundColor: '#f8f9fa'}}>
                              {formatCurrency(item.net_transfer)}
                            </td>

                            <td>{formatCurrency(item.expenses)}</td>

                            {globalHasHNO && (
                                <td>{formatCurrency(hnoAmount)}</td>
                            )}

                            <td style={{fontWeight: 'bold', color: '#2c3e50'}}>
                                {formatCurrency(customTotal)}
                            </td>

                            <td className={soldeReel >= 0 ? styles.positive : styles.negative} style={{fontWeight: 'bold'}}>
                              {formatCurrency(soldeReel)}
                            </td>
                          </tr>

                          {isExpanded && hasDetails && (
                            <tr className={styles.detailsRow}>
                              <td colSpan={
                                // Calcul dynamique du colSpan selon les colonnes affichées
                                8 + (hasAnyAjustement ? 1 : 0) + (globalHasHNO ? 1 : 0) + 2
                              }>
                                <div className={styles.detailsContainer}>
                                  <p className={styles.detailsTitle}>
                                    {historyClientFilter !== 'Tous' ? `Détail pour ${historyClientFilter} :` : 'Détail par Facture :'}
                                  </p>
                                  <table className={styles.subTable}>
                                    <thead>
                                      <tr>
                                        <th>Partenaire</th>
                                        <th>Statut</th>
                                        <th>Hors HNO (HT / Jours)</th>
                                        {hasHNO && <th>HNO (HT / Jours)</th>}
                                        <th>Total HT</th>
                                        <th>Total TTC</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(() => {
                                        const sortedDetails = [...item.missions_details].sort((a, b) => 
                                          (a.clientName || '').localeCompare(b.clientName || '')
                                        );

                                        const clientCounts = {};
                                        sortedDetails.forEach(m => {
                                          clientCounts[m.clientName] = (clientCounts[m.clientName] || 0) + 1;
                                        });

                                        const renderedClients = new Set();

                                        return sortedDetails.map((mission, idx) => {
                                          const isFirstClientRow = !renderedClients.has(mission.clientName);
                                          if (isFirstClientRow) {
                                            renderedClients.add(mission.clientName);
                                          }

                                          return (
                                            <tr key={idx}>
                                              {isFirstClientRow && (
                                                <td 
                                                  rowSpan={clientCounts[mission.clientName]} 
                                                  style={{ verticalAlign: 'middle', fontWeight: 'bold', borderRight: '1px solid #e2e8f0' }}
                                                >
                                                  {mission.clientName}
                                                </td>
                                              )}
                                              
                                              <td>
                                                <span className={`${styles.badge} ${getStatusBadgeClass(mission.stateFacture)}`}>
                                                  {mission.stateFacture || 'Inconnu'}
                                                </span>
                                                {mission.isNDF && (
                                                  <span style={{marginLeft:'8px', backgroundColor:'#3498db', color:'white', padding:'2px 6px', borderRadius:'4px', fontSize:'0.7rem', fontWeight:'bold'}}>
                                                    NDF
                                                  </span>
                                                )}
                                              </td>
                                              
                                              <td>
                                                <div>{formatCurrency(mission.detail_normal || 0)}</div>
                                                {mission.isNDF ? (
                                                  <small style={{color: '#3498db', fontWeight: 'bold'}}>Frais refacturés</small>
                                                ) : (
                                                  <small style={{opacity: 0.7}}>{mission.detail_qty_normal || 0} j</small>
                                                )}
                                              </td>
                                              
                                              {hasHNO && (
                                                <td>
                                                  {!mission.isNDF ? (
                                                    <>
                                                      <div style={{color: '#27ae60'}}>{formatCurrency(mission.detail_hno || 0)}</div>
                                                      <small style={{opacity: 0.7}}>{mission.detail_qty_hno || 0} j</small>
                                                    </>
                                                  ) : (
                                                    <div style={{color: '#95a5a6'}}>-</div>
                                                  )}
                                                </td>
                                              )}
                                              
                                              <td style={{fontWeight:'bold'}}>
                                                {formatCurrency((mission.detail_normal || 0) + (mission.detail_hno || 0))}
                                              </td>
                                              
                                              <td style={{fontWeight:'bold'}}>
                                                {formatCurrency((mission.detail_ttc_normal || 0) + (mission.detail_ttc_hno || 0))}
                                              </td>
                                            </tr>
                                          );
                                        });
                                      })()}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.loadingText}>Aucune donnée disponible pour ce filtre.</p>
            )}
          </div>
        </div>
      )}

      <div className={styles.helpBox}>
        <div className={styles.helpIcon}>💡</div>
        <div className={styles.helpContent}>
          <h3 className={styles.helpTitle}>Comprendre vos soldes</h3>
          <p className={styles.helpText}>
            <strong>Solde Disponible :</strong> Correspond à votre trésorerie réelle (factures payées - charges).<br />
            <strong>Solde Prévisionnel :</strong> Votre trésorerie une fois toutes les factures encaissées.<br />
            <strong>Solde en attente :</strong> Montant des factures en attente de paiement.
          </p>
        </div>
      </div>
      
      {selectedMissionId && (
        <MissionDetailsModal 
          missionId={selectedMissionId} 
          onClose={() => setSelectedMissionId(null)} 
        />
      )}

    </div>
  );
};

export default AppHomePage;